<script>
import NoResults from '@components/notifications/no-results.vue';
import formatPhoneNumber from '@utils/format-phone-number';
import {
  modalMethods,
  prospectComputed,
  prospectMethods,
} from '@state/helpers';

export default {
  components: {
    NoResults,
  },
  props: {
    prospect: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      formatPhoneNumber: formatPhoneNumber,
    };
  },
  computed: {
    ...prospectComputed,
    comms() {
      return this.prospect?.emails?.concat(this.prospect?.texts);
    },
  },
  mounted() {
    if (this.prospect) {
      this.getProspect(this.prospect._id).catch((err) =>
        this.handleGetProspectError(err)
      );
    }
  },
  methods: {
    ...modalMethods,
    ...prospectMethods,
    msgRecipient(msg) {
      const recipient = msg.sendgrid_og_id
        ? this.truncateMsg(msg.to_email)
        : this.truncateMsg(msg.to);
      return recipient || 'Pending...';
    },
    msgStatus(msg) {
      const status = msg.sendgrid_og_id || msg.sid ? msg.status : 'replied';
      return status === 'received' ? 'delivered' : status?.replace('_', ' ');
    },
    msgSubject(msg) {
      if (msg?.sid) {
        return this.truncateMsg(msg.body);
      } else {
        return this.truncateMsg(msg.subject);
      }
    },
    msgTimestamp(msg) {
      return msg?.timestamp;
    },
    truncateMsg(msg) {
      const maxChars = 73;
      if (msg) {
        return msg?.length <= maxChars
          ? msg
          : msg?.substr(0, maxChars + 1) + '...';
      } else {
        return null;
      }
    },
    handleGetProspectError(err) {
      if (err.response?.status === 502) {
        this.setNotifyModalContext({
          textMain: 'Something went wrong!',
          textSecond: 'Please try again.',
          retry: true,
          request: 'getProspect',
          params: { id: this.prospect._id },
        });
        this.$bvModal.show('notify-modal');
      }
    },
  },
};
</script>

<template>
  <div class="prospect-comm-history">
    <h3 class="section-title">
      Communication History
    </h3>
    <ul class="list-comm-history" v-if="comms.length">
      <li v-for="(msg, i) in comms" :key="i">
        <div :class="['delivery-status', msgStatus(msg)]">
          <div :class="['icon-delivery-status', msgStatus(msg) || 'pending']" />
          <span>{{ msgStatus(msg) || 'Sending' }}</span>
        </div>
        <div class="recipient">
          To:
          <span v-if="msg.sid">{{
            formatPhoneNumber(msgRecipient(msg)) || 'Pending...'
          }}</span>
          <span v-else>{{ msgRecipient(msg) }}</span>
          <span class="subject">{{ msgSubject(msg) }}</span>
        </div>
        <div class="timestamp">{{ msgTimestamp(msg) }}</div>
      </li>
    </ul>
    <div class="row" v-else>
      <div class="col-7">
        <NoResults>
          <h3 slot="title">No Messages</h3>
          <p slot="text">
            No emails or SMS messages have been sent yet.
          </p>
        </NoResults>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.prospect-comm-history {
  .list-comm-history {
    color: $color-dark-gray-one;
    font-size: 1.7rem;
    font-style: normal;
    font-weight: normal;
    letter-spacing: -0.233333px;
    line-height: 2.8rem;
    margin-bottom: 2rem;
    mix-blend-mode: normal;
    position: relative;

    li {
      display: flex;
      margin-bottom: 3.2rem;
    }

    .delivery-status {
      display: flex;
      font-size: 1.7rem;
      letter-spacing: 0.116667px;
      line-height: 2.4rem;
      margin-right: 4rem;

      &.open,
      &.opened {
        margin-right: 5.3rem;
      }

      &.replied {
        margin-right: 5.5rem;
      }

      span {
        text-transform: capitalize;
      }
    }

    .icon-delivery-status {
      align-items: center;
      border-radius: $border-radius-circle;
      box-sizing: border-box;
      display: flex;
      height: 1.3rem;
      justify-content: center;
      left: 0;
      margin-right: 0.7rem;
      position: relative;
      top: 0.4rem;
      width: 1.3rem;

      &.delivered {
        background-color: $color-neon-green;
      }

      &.open,
      &.opened {
        background-color: $color-green;
      }

      &.replied {
        background-color: $color-yellow-one;
      }

      &.pending {
        background-color: $color-primary;
      }

      &.not.delivered {
        background-color: $color-red;
      }
    }

    .recipient {
      font-size: 1.7rem;
      font-weight: bold;
      line-height: 2.4rem;
      margin-right: 5.2rem;

      span,
      .subject {
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 2.4rem;
      }

      .subject {
        display: block;
        font-weight: 300;
      }
    }

    .timestamp {
      font-size: 1.7rem;
      letter-spacing: 0.116667px;
      line-height: 2.4rem;
    }
  }

  .no-results {
    align-items: center;
    background-color: $color-off-white;
    border-radius: $base-border-radius-md;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-left: 0;
    padding: 2rem;
    width: 100%;
  }
}
</style>
